import React from 'react'
import clsx from 'clsx'
import { CircularProgress, useTheme } from '@mui/material'
import { Send } from 'iconsax-react'
import { useAppSelector } from '@SonarCX/hooks/redux'

import Button from '../../../../atoms/Button2'

import { useCanSend } from '@SonarCX/providers/CanSendProvider'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useOnNotes } from '@SonarCX/providers/OnNotesProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useEnterMode } from '@SonarCX/providers/EnterModeProvider'
import { selectChatControls } from '@SonarCX/slices/chatControlsSlice'
import useStyles from './styles'

const Actions = ({
  type,
  onTypeSet,
  sendMessage,
  disabled,
  createTask,
  doneUploading,
  session,
  canAddNotes,
  children
}) => {
  const theme = useTheme()

  // Gets props from Controls
  const { file, message } = useAppSelector(selectChatControls)
  const { canSend } = useCanSend()
  const { state: editState, submit: submitEdit } = useChatEditState()
  const { onNotes: allOnNotes } = useOnNotes()
  const { resetTemp } = useEnterMode()
  const onNotes = React.useMemo(
    () => Boolean(allOnNotes[session]),
    [allOnNotes, session]
  )
  const { isSmall } = useBreakpoint()

  const classes = useStyles({ smallMedia: isSmall })

  const isTask = type === 'Task'

  const handleCreateTaskClick = () => {
    createTask()
    onTypeSet('Live')
  }

  const buttonDisabled = React.useMemo(() => {
    if (onNotes) {
      return !canAddNotes
    }

    let hasContent
    if (editState.editing) {
      hasContent = editState.newMessage.trim().length > 0 || file.length > 0
    } else {
      hasContent =
        type !== 'Live' || message.trim().length > 0 || file.length > 0
    }

    if (editState.editing) {
      return !hasContent
    }

    return disabled || !canSend || !doneUploading || !hasContent
  }, [
    onNotes,
    canAddNotes,
    message,
    file,
    disabled,
    canSend,
    doneUploading,
    editState,
    type
  ])

  return (
    <div className={classes.root}>
      <div className={classes.buttons}>
        {!onNotes && isTask && (
          <Button
            onClick={() => onTypeSet('Live')}
            className={classes.send}
            disabled={disabled}
            invert='true'
          >
            CANCEL
          </Button>
        )}

        <Button
          onClick={() => {
            if (!onNotes && isTask) {
              handleCreateTaskClick()
            } else {
              if (editState.editing) {
                submitEdit()
                resetTemp()
              } else {
                sendMessage(null, 'clicked')
              }
            }
          }}
          className={clsx(
            classes.send,
            isSmall && !onNotes && !isTask && classes.sendMsg,
            !buttonDisabled && classes.submitButton
          )}
          disabled={buttonDisabled}
        >
          {onNotes
            ? 'ADD NOTE'
            : isTask
            ? 'ADD TASK'
            : editState.editing
            ? 'EDIT'
            : isSmall
            ? ''
            : 'SEND'}

          {!isTask && !onNotes && (
            <Send variant='Bold' size={theme.size.base} className='icon' />
          )}

          {!doneUploading && (
            <CircularProgress
              className='icon'
              style={{ height: '20px', width: '20px' }}
            />
          )}
        </Button>

        {children}
      </div>
    </div>
  )
}

export default Actions
